<script setup>
import { computed, defineProps } from 'vue';

const props = defineProps({
	data: {
		type: Object,
		required: true,
	},
});

const categories = computed(() => props.data?.categories ?? []);

const chartSeries = computed(() =>
	(props.data?.series ?? []).map((item) => ({
		name: item.name,
		data: categories.value.map((category, index) => ({
			x: category,
			y: item.data?.[index] ?? 0,
		})),
	}))
);

const CHART_COLORS = ['#FF6967', '#FF9D6D', '#FFE68C', '#D0DB54', '#9EDF93'];

const stackedTotals = computed(() => {
	const series = props.data?.series ?? [];
	return categories.value.map((_, index) =>
		series.reduce((sum, item) => sum + (item.data?.[index] ?? 0), 0)
	);
});

const yaxisMaxValue = computed(() => {
	const maxTotal = Math.max(...stackedTotals.value, 0);
	if (maxTotal === 0) return 5;
	return Math.ceil(maxTotal * 1.2);
});

const MAX_LABEL_LENGTH = 25;

const truncateLabel = (value) => {
	if (!value) return '';
	return value.length > MAX_LABEL_LENGTH
		? `${value.substring(0, MAX_LABEL_LENGTH)}…`
		: value;
};

const chartHeight = computed(() => Math.max(400, categories.value.length * 72));

const chartOptions = computed(() => ({
	grid: {
		show: false,
		padding: {
			bottom: 24,
		},
	},
	colors: CHART_COLORS,
	chart: {
		type: 'bar',
		stacked: true,
		toolbar: { show: true },
		zoom: { enabled: true },
	},
	plotOptions: {
		bar: {
			dataLabels: {
				position: 'top',
				total: {
					enabled: false,
					style: {
						fontSize: '12px',
						fontWeight: 400,
						colors: '#344256',
					},
				},
			},
		},
	},
	dataLabels: {
		enabled: true,
		formatter: function (val, opts) {
			const chartHeight = opts.w.globals.gridHeight;
			const maxValue = opts.w.globals.maxY;
			const barHeight = (val / maxValue) * chartHeight;
			if (barHeight < 15) {
				return '';
			}
			return val;
		},
	},
	xaxis: {
		categories: categories.value,
		crosshairs: {
			show: false,
		},
		tooltip: {
			enabled: false,
		},
		labels: {
			rotate: -45,
			rotateAlways: true,
			hideOverlappingLabels: false,
			maxHeight: 120,
			offsetY: 4,
			style: {
				fontSize: '11px',
				colors: '#344256',
			},
			formatter: truncateLabel,
		},
	},
	tooltip: {
		shared: true,
		intersect: false,
		x: {
			formatter: (value) => value,
		},
	},
	yaxis: {
		min: 0,
		max: yaxisMaxValue.value,
		tickAmount: Math.min(yaxisMaxValue.value, 10),
	},
	legend: {
		show: true,
		position: 'top',
		itemMargin: {
			horizontal: 10,
			vertical: 20,
		},
	},
	fill: { opacity: 1 },
}));
</script>
<template>
	<div class="bar-chart w-full">
		<apexchart
			type="bar"
			:height="chartHeight"
			:series="chartSeries"
			:options="chartOptions"
		/>
	</div>
</template>
